import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import { Logo, Panel } from '../_components';
import { ROUTE as APP_ROUTE } from '~/app/routes';
import { Loading, NotificationList } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { LoginAuthMethod } from '~/lib/session';
import { ROUTE } from '~/public/routes';
import { UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Headline, Paragraph } from '~/styles';
import { delay } from '~/util';

interface Params {
  code?: string;
}

const REFERRER_POLICY_META_ID = 'magic-link-referrer-policy';
const MAGIC_LINK_NOTIFICATION_GROUP = 'page-magic-link';
const MINIMUM_AUTHENTICATION_DURATION_MS = 1000;
const COMPLETION_ANIMATION_DURATION_MS = 450;

type AuthenticationStatus = 'loading' | 'complete' | 'error';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

export const PageMagicLink: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const { setSession, signIn } = useSessionStore();
  const navigate = useNavigate();
  const { code } = useParams<keyof Params>() as Params;
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatus>('loading');

  useLayoutEffect(() => {
    let referrerPolicy = document.getElementById(REFERRER_POLICY_META_ID) as HTMLMetaElement | null;

    if (!referrerPolicy) {
      referrerPolicy = document.createElement('meta');
      referrerPolicy.id = REFERRER_POLICY_META_ID;
      referrerPolicy.name = 'referrer';
      referrerPolicy.content = 'no-referrer';
      document.head.appendChild(referrerPolicy);
    }

    return () => referrerPolicy?.remove();
  }, []);

  const magicLinkMutation = useMutation({
    mutationFn: async (token: string) => {
      const [response] = await Promise.all([
        UserRepository.confirmLoginEmailChallenge(token),
        delay(MINIMUM_AUTHENTICATION_DURATION_MS),
      ]);
      return response;
    },
    onSuccess: async (response) => {
      setAuthenticationStatus('complete');
      setSession(response.sessionId, response.dashboardUserId, {
        loginAuthMethod: LoginAuthMethod.PASSWORD,
        mfaStatus: response.mfaStatus,
      });

      await delay(COMPLETION_ANIMATION_DURATION_MS);
      try {
        await signIn();
        navigate(APP_ROUTE.ROOT, { state: { from: 'auth' } });
      } catch (error) {
        setAuthenticationStatus('error');
        addDangerNotification({
          content: error instanceof Error ? error.message : 'Unable to sign in. Please try again.',
          display: MAGIC_LINK_NOTIFICATION_GROUP,
          timeout: -1,
          withClose: false,
        });
      }
    },
    onError: (error: unknown) => {
      setAuthenticationStatus('error');
      const message =
        typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'Unable to confirm this sign in. Please try again.';

      addDangerNotification({
        content: message,
        display: MAGIC_LINK_NOTIFICATION_GROUP,
        timeout: -1,
        withClose: false,
      });
    },
    retry: false,
  });

  const { mutate } = magicLinkMutation;
  useEffect(() => {
    if (!code) {
      navigate(ROUTE.LOGIN, { replace: true });
      return;
    }

    mutate(code);
  }, [code, mutate, navigate]);

  const isComplete = authenticationStatus === 'complete';
  const isError = authenticationStatus === 'error';

  return (
    <>
      <LogoWrapper>
        <AdditionalLogo size="24px" variant="full" />
      </LogoWrapper>
      <Panel>
        <HiddenLogo size="24px" />
        <Loading minHeight={false} isLoading={!isError && !isComplete}>
          <Flex flexDirection="column" gap={8} mb={8}>
            <Headline fullWidth size="small">
              {isError ? 'Invalid link' : 'Authenticated'}
            </Headline>
            <Paragraph>
              {isError ? 'Sign in again to request a new link.' : 'Your sign in has been confirmed. Redirecting…'}
            </Paragraph>
          </Flex>
        </Loading>
        <NotificationList display={MAGIC_LINK_NOTIFICATION_GROUP} variant="light" />
        {isError && (
          <Button fullWidth onClick={() => navigate(ROUTE.LOGIN)}>
            Return to login
          </Button>
        )}
      </Panel>
    </>
  );
};
